import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { type FeatureExposedProps, type FeatureExposedResponse } from '../../types';

/**
 * useFeatureExposed
 *
 * Hook that indicates a user is enrolled into a particular feature.
 * @param experimentKey name of the past experiment
 * @param group name of the group that this version of the code belongs to
 * @param conditions conditions that must be met for the feature exposed to be triggered
 * @param metadata additional metadata to include in the feature exposed event
 * @returns FeatureExposedResponse
 *
 * @example
 * const { hasFired } = useFeatureExposed("experiment-name", "group", { conditions: [isInView] });
 */
export function useFeatureExposed(
	experimentKey: string,
	group: 'Control' | 'Test' | string,
	{ conditions, metadata }: FeatureExposedProps = {},
): FeatureExposedResponse {
	const fired = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (fired.current) {
			return;
		}
		const featureExposurePreconditionsMet =
			!conditions || conditions.filter((condition) => !condition).length === 0;
		if (!featureExposurePreconditionsMet) {
			return;
		}
		// fire manual exposure event.
		createAnalyticsEvent({
			source: experimentKey,
			actionSubject: 'feature',
			action: 'exposed',
			actionSubjectId: experimentKey,
			attributes: {
				group,
				...metadata,
			},
			eventType: 'track',
		}).fire();

		fired.current = true;
	}, [experimentKey, group, conditions, metadata, createAnalyticsEvent]);

	return {
		hasFired: fired.current,
	};
}
